import React from 'react';
import Phone from '@material-ui/icons/PhoneOutlined';
import GridItem from 'components/Grid/GridItem';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import { HeadingSBold, BodyS, HeadingMBold, BodyM, CaptionBold } from 'components/Typography/Typography';
import { translate } from 'components/Translation';
import GridContainer from 'components/Grid/GridContainer';
import Badge from 'components/Badge/Badge';
import styles from 'assets/jss/material-dashboard-pro-react/views/numberDetails/detailsStyle';
import { makeStyles } from '@material-ui/core/styles';
import CustomLink from 'components/CustomLink/CustomLink';
import CanPerform from 'components/CanPerform/CanPerform';
import { CorpUserData } from 'utils/api/models/users/corpUserData';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import classNames from 'classnames';
import DetachProfileDialog from './DetachProfileDialog';
import Tooltip from '@material-ui/core/Tooltip';
import Info from '@material-ui/icons/InfoOutlined';

// @ts-ignore
const useStyles = makeStyles(styles);

export interface INumberInformationCardProps {
	accountName: string;
	accountNumber: string;
	corpUserData: CorpUserData | null;
	phoneNumber: string;
	isAccountSuspended: boolean;
	isSimCardDisconnected: boolean;
}

const NumberInformationCard = ({
	accountName,
	accountNumber,
	corpUserData,
	phoneNumber,
	isSimCardDisconnected,
	isAccountSuspended,
}: INumberInformationCardProps) => {
	const classes = useStyles();

	return (
		<GridItem xs={12} md={6}>
			<Card className={classes.informationCard}>
				<CardBody className={classes.cardBody}>
					<div className={classes.informationSubheader}>
						<HeadingSBold disableMargin>{translate('numbers.details.numberInformation')}</HeadingSBold>
					</div>
					<div className={classes.dataContainer}>
						<div
							className={classNames(classes.dataIcon, {
								[classes.numberDisconnectedIcon]: isSimCardDisconnected,
							})}
						>
							<Phone className={classes.grayIcon} />
						</div>
						<div className={classes.dataText}>
							<div className={classes.usernameWrapper}>
								<HeadingMBold disableMargin color={isAccountSuspended ? 'danger' : 'primary-light'}>
									{phoneNumber}
								</HeadingMBold>
								<ConditionalRender
									show={isSimCardDisconnected}
									onTrue={() => (
										<div className={classes.numberDisconnectedWrapper}>
											<CaptionBold color="danger" disableMargin>
												{translate('number.disconnected')}
											</CaptionBold>
											<Tooltip
												title={
													<BodyS color="primary-dark" disableMargin>
														{translate('number.disconnected.tooltip')}
													</BodyS>
												}
												className={classes.simCardDisconnectedTooltip}
											>
												<Info />
											</Tooltip>
										</div>
									)}
								/>
								<ConditionalRender
									show={isAccountSuspended}
									onTrue={() => (
										<Badge filled color="danger">
											<>
												{translate('number.not.active')}
												<Tooltip
													title={
														<BodyS color="primary-dark" disableMargin>
															{translate('number.not.active.tooltip')}
														</BodyS>
													}
													className={classes.tooltip}
												>
													<Info />
												</Tooltip>
											</>
										</Badge>
									)}
								/>
								<ConditionalRender
									show={!!corpUserData && !!corpUserData.departmentName && !corpUserData.userName}
									onTrue={() => (
										<Badge filled hexColor={corpUserData?.departmentUiColor}>
											{corpUserData?.departmentName}
										</Badge>
									)}
								/>
							</div>
							<BodyM>{accountName}</BodyM>
						</div>
					</div>
					<ConditionalRender
						show={!!corpUserData?.privateProfileMaster}
						onTrue={() => (
							<div className={classes.dataContainer}>
								<div className={classes.dataIcon} />
								<div className={classes.dataText}>
									<GridContainer justify="flex-start">
										<GridItem xs={12}>
											<div className={classes.detachProfileContainer}>
												<BodyS color="secondary-light">
													{translate('numbers.details.numberRelated')}
												</BodyS>
												<DetachProfileDialog
													phoneNumber={phoneNumber}
													masterNumber={corpUserData?.privateProfileMaster!}
												/>
											</div>
										</GridItem>
									</GridContainer>
								</div>
							</div>
						)}
					/>
					<div className={classes.dataContainer}>
						<div className={classes.dataIcon} />
						<div className={classes.dataText}>
							<GridContainer justify="flex-start">
								<ConditionalRender
									show={!!corpUserData}
									onTrue={() => (
										<GridItem xs={6}>
											<div className={classes.billingAccount}>
												<BodyS color="secondary-light" disableMargin>
													{translate('numbers.details.billingAccount')}
												</BodyS>
											</div>
											<CanPerform
												action="number-details:see-billing-account-details"
												yes={
													<CustomLink
														to={`/dashboard/active-services/${accountNumber}/${corpUserData?.accountId}/numbers`}
														color="brand"
														type="bodyMBold"
													>
														{accountNumber}
													</CustomLink>
												}
												no={<BodyS>{accountNumber}</BodyS>}
											/>
										</GridItem>
									)}
								/>
							</GridContainer>
						</div>
					</div>
				</CardBody>
			</Card>
		</GridItem>
	);
};

export default NumberInformationCard;
